import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, HostListener, inject, OnDestroy, signal, untracked, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatIconButton, MatButton } from '@angular/material/button';
import { UserProfileStore } from '@state/user-profile.store';
import { UserStatsStore } from '@state/user-stats.store';
import { AuthService } from '../../services/AuthService';
import { NotificationItem } from '@shared/models/notification.model';
import { NotificationService } from '@core/services/notification.service';
import { SearchService } from '@core/services/search.service';
import { ThemeService } from '@core/services/theme.service';
import { AdminService } from '@core/services/admin.service';
import { LucideIconsModule } from '@shared/lucide-icons.module';
import { SearchDropdownComponent, SearchSelection } from '@shared/components/search-dropdown/search-dropdown.component';
import { getAvatarInitials } from '@shared/utils/avatar-initials.util';

interface GroupedNotifications {
  label: string;
  notifications: NotificationItem[];
}

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.html',
  imports: [
    FormsModule,
    MatIconButton,
    MatButton,
    LucideIconsModule,
    SearchDropdownComponent,
    RouterLink,
    RouterLinkActive
  ],
  styleUrls: ['./toolbar.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToolbarComponent implements AfterViewInit, OnDestroy {

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('inlineSearchInput') inlineSearchInput?: ElementRef<HTMLInputElement>;

  private readonly router = inject(Router);
  private readonly userProfileStore = inject(UserProfileStore);
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);
  private readonly searchService = inject(SearchService);
  private readonly themeService = inject(ThemeService);
  private readonly userStatsStore = inject(UserStatsStore);
  private readonly adminService = inject(AdminService);

  // Auth state
  isAuthenticated = this.authService.isAuthenticated;

  // Scroll state for shadow effect
  isScrolled = signal(false);
  readonly currentUrl = signal(this.router.url);

  // User info - connected to AuthService for authenticated user data
  userName = computed(() => {
    return this.userProfileStore.nickname().trim() || 'Member';
  });
  userAvatar = signal(''); // Empty = use initials
  postCount = this.userStatsStore.postCount;
  replyCount = this.userStatsStore.replyCount;
  helpfulCount = this.userStatsStore.helpfulCount;
  savedCount = this.userStatsStore.savedCount;
  memberSince = computed(() => {
    const user = this.authService.currentUser();
    if (user?.createdAt) {
      return this.formatMemberSince(user.createdAt);
    }
    return 'Recently joined';
  });
  readonly isDarkMode = computed(() => this.themeService.resolvedTheme() === 'dark');
  readonly canShowDesktopNav = computed(() => {
    const url = this.currentUrl();
    if (url.startsWith('/onboarding') || url.startsWith('/accept-terms')) {
      return false;
    }

    return this.authService.authReady() && this.isAuthenticated();
  });
  readonly canShowPublicDesktopNav = computed(() => this.authService.authReady() && !this.isAuthenticated());
  readonly isAdmin = this.adminService.isAdmin;
  readonly brandRoute = computed(() => (this.isAuthenticated() ? '/home' : '/landing'));
  isOnline = signal(true); // Online status indicator
  userMenuOpen = signal(false);

  // Search - now with inline expanding search
  searchOpen = signal(false);
  searchExpanded = signal(false); // For inline search bar expansion
  searchQuery = signal('');
  readonly activeCommunityId = computed(() => {
    const match = this.currentUrl().match(/^\/(?:category|community)\/([^/?#]+)/);
    return match?.[1];
  });
  readonly currentSearchContext = computed<'global' | 'community'>(() =>
    this.activeCommunityId() ? 'community' : 'global'
  );

  // Notifications
  notificationsOpen = signal(false);
  notifications = this.notificationService.notifications;
  notificationsLoading = this.notificationService.loading;
  unreadCount = this.notificationService.unreadCount;
  unreadBadgeLabel = this.notificationService.unreadBadgeLabel;

  // Grouped notifications by time
  groupedNotifications = computed((): GroupedNotifications[] => {
    const notifs = this.notifications();
    const now = new Date();
    const today: NotificationItem[] = [];
    const yesterday: NotificationItem[] = [];
    const earlier: NotificationItem[] = [];

    notifs.forEach(n => {
      const createdAt = new Date(n.createdAt);
      const diff = now.getTime() - createdAt.getTime();
      const hours = diff / (1000 * 60 * 60);

      if (hours < 24) {
        today.push(n);
      } else if (hours < 48) {
        yesterday.push(n);
      } else {
        earlier.push(n);
      }
    });

    const groups: GroupedNotifications[] = [];
    if (today.length) groups.push({ label: 'Today', notifications: today });
    if (yesterday.length) groups.push({ label: 'Yesterday', notifications: yesterday });
    if (earlier.length) groups.push({ label: 'Earlier', notifications: earlier });

    return groups;
  });

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects || event.url);
        this.userMenuOpen.set(false);
        this.searchOpen.set(false);
        this.notificationsOpen.set(false);
      }
    });

    effect(() => {
      const authReady = this.authService.authReady();
      const isAuthenticated = this.isAuthenticated();
      if (authReady && isAuthenticated) {
        untracked(() => this.adminService.refreshAdminStatus());
      }
    });

    effect(() => {
      const authReady = this.authService.authReady();
      const isAuthenticated = this.isAuthenticated();

      untracked(() => {
        if (authReady && isAuthenticated) {
          this.notificationService.startPollingUnreadCount(60000);
          return;
        }
        this.notificationService.stopPollingUnreadCount();
      });
    });
  }

  ngOnDestroy(): void {
    this.notificationService.stopPollingUnreadCount();
  }

  ngAfterViewInit() {
    // Auto-focus search input when opened
    if (this.searchOpen() && this.searchInput) {
      setTimeout(() => this.searchInput?.nativeElement.focus(), 100);
    }
  }

  /**
   * Listen for scroll events to add shadow on scroll
   */
  @HostListener('window:scroll')
  onWindowScroll() {
    this.isScrolled.set(window.scrollY > 10);
  }

  /**
   * Expand inline search bar (desktop)
   */
  expandSearch() {
    this.searchExpanded.set(true);
    setTimeout(() => this.inlineSearchInput?.nativeElement.focus(), 100);
  }

  /**
   * Collapse inline search bar
   */
  collapseSearch() {
    if (!this.searchQuery().trim()) {
      this.searchExpanded.set(false);
    }
  }

  /**
   * Open search overlay (mobile)
   */
  openSearch() {
    this.searchOpen.set(true);
    // Focus input after animation
    setTimeout(() => this.searchInput?.nativeElement.focus(), 100);
  }

  /**
   * Close search overlay
   */
  closeSearch() {
    this.searchOpen.set(false);
    this.searchQuery.set('');
  }

  /**
   * Toggle user menu dropdown
   */
  toggleUserMenu(event: Event) {
    event.stopPropagation();
    const nextOpen = !this.userMenuOpen();
    this.userMenuOpen.set(nextOpen);
    if (nextOpen) {
      this.adminService.refreshAdminStatus(true);
      this.userStatsStore.load();
    }
  }

  /**
   * Close user menu dropdown
   */
  closeUserMenu() {
    this.userMenuOpen.set(false);
  }

  navigateFromUserMenu(route: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeUserMenu();
    void this.router.navigateByUrl(route);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.closeUserMenu();
  }

  /**
   * Perform search
   */
  performSearch() {
    const query = this.searchQuery().trim();
    if (query) {
      this.searchService.saveRecentSearch(query);
      this.router.navigate(['/search'], { queryParams: { q: query } });
      this.closeSearch();
      this.searchExpanded.set(false);
    }
  }

  onSuggestionSelected(selection: SearchSelection): void {
    this.router.navigate(selection.route, selection.params ? { queryParams: selection.params } : undefined);
    this.closeSearch();
  }

  openSavedPosts() {
    this.router.navigate(['/me/saved']);
  }

  toggleNotifications() {
    this.closeNotifications();
    void this.router.navigate(['/activity']);
  }

  /**
   * Open notifications panel
   */
  openNotifications() {
    this.notificationsOpen.set(true);
    this.notificationService.refreshNotifications();
  }

  /**
   * Close notifications panel
   */
  closeNotifications() {
    this.notificationsOpen.set(false);
  }

  /**
   * Handle notification click
   */
  handleNotification(notification: NotificationItem) {
    this.notificationService.markRead(notification.id);

    // Navigate to post
    if (notification.postId) {
      this.router.navigate(['/post', notification.postId]);
    }

    this.closeNotifications();
  }

  /**
   * Dismiss a single notification
   */
  dismissNotification(event: Event, notification: NotificationItem) {
    event.stopPropagation();
    this.notificationService.deleteNotification(notification.id);
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead() {
    this.notificationService.markAllRead();
  }

  /**
   * Get notification icon based on type
   */
  getNotificationIcon(type: NotificationItem['type']): string {
    const icons: Record<string, string> = {
      reply: 'reply',
      mention: 'at-sign',
      helpful: 'thumbs-up',
      saved: 'bookmark',
      follow: 'bookmark',
      system: 'info',
      moderation: 'shield',
    };
    return icons[type] || 'bell';
  }
  /**
   * Toggle theme (light/dark)
   */
  toggleTheme() {
    this.themeService.toggle();
  }

  /**
   * Sign out
   */
  signOut() {
    this.closeUserMenu();
    this.userStatsStore.reset();
    this.authService.logout();
  }


  /**
   * Get user initials for avatar
   */
  getInitials(): string {
    return getAvatarInitials(this.userName());
  }

  /**
   * Format timestamp to relative time
   */
  formatTime(timestamp: Date | string): string {
    const time = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diff = now.getTime() - time.getTime();

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return time.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Format createdAt date to "Member since" format (e.g., "Jan 2024")
   */
  private formatMemberSince(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Recently joined';
    }
  }
}





